import type { BirdiePastRound } from './pastRounds'
import { countFromKey, glyphsForKey, PUTTS_PER_ROUND, type PuttMark } from './tileViewModel'

/**
 * Birdie's recent form (B19 section 5) — the panel behind the stats button and
 * the first block inside the history sidebar.
 *
 * WHY THESE FOUR ROWS. A marble game's recent form is "which marble won, how
 * often", and that is exactly the number the owner said means nothing here
 * (2026-09-08: "showing the past results does not make much sense as it changes
 * each time with the probability"). What DOES compare across rounds that are
 * repriced every round is how the three putts fell: none, one, two, or all
 * three in. So the rows are the COUNT market's four options — the same four the
 * board's right-hand column prices, in the same top-to-bottom order — and the
 * two footer figures are the round's difficulty (the average make chance in the
 * window) and its ceiling (the biggest price that actually paid).
 *
 * Pure, and free of both React and `Intl`: every number below is a count or a
 * fraction, and the panel formats them through the caller's one `safeLocale`
 * formatter pair.
 */

/** The window the panel describes. The spec's number, not the store's. */
export const BIRDIE_FORM_WINDOW = 50

/** The four count options, top to bottom, exactly as the board's bars run. */
export const BIRDIE_FORM_KEYS = ['IN0', 'IN1', 'IN2', 'IN3'] as const

export interface BirdieFormRow {
  /** `IN0`…`IN3` — also the marbleColors key the row's name comes from. */
  key: string
  /** The row's label: its holed balls (`IN0` keeps three missed ones). */
  marks: PuttMark[]
  count: number
  /** 0..1 — the track bar's fill, and the row's percentage. */
  fraction: number
}

export interface BirdieRecentFormView {
  /** Rounds actually counted. Never larger than `BIRDIE_FORM_WINDOW`. */
  sampleSize: number
  /** Always four rows, always in `BIRDIE_FORM_KEYS` order. */
  rows: BirdieFormRow[]
  /** 0..1 — the mean make chance over the window. */
  avgMakeRate: number
  /** The biggest price that paid in the window, unformatted. */
  biggestPaid?: number
}

/**
 * How many of the round's three putts dropped.
 *
 * The count OPTION is the authority — it is what the engine settled position 1
 * to, and it is the same value the count bars were priced against. The pattern
 * is the fallback for a round whose count winner the widget could not key
 * (a marble id outside the twelve), because the three putts are the same fact
 * told twice. A round with neither is not counted at all rather than being
 * filed under a guess: it would move a percentage a player reads as truth.
 */
function holedCount(round: BirdiePastRound): number | undefined {
  const fromKey = countFromKey(round.countKey ?? '')
  if (fromKey !== undefined) return fromKey
  if (round.marks.length !== PUTTS_PER_ROUND) return undefined
  let made = 0
  for (const mark of round.marks) if (mark === 'holed') made++
  return made
}

/**
 * What an option paid on that round — boosted if the round's Lightning was on
 * it, exactly as `buildRoundCardView` decides it for the winner tiles. The
 * "biggest paid" figure has to be the number a player could have collected, so
 * a boosted round's headline is its boosted price.
 */
function paidFor(round: BirdiePastRound, key: string, base: number): number {
  for (const boost of round.boosts) if (boost.key === key) return boost.paid
  return base
}

/**
 * The window's shape.
 *
 * `rounds` is expected newest-first (`birdiePastRounds`' own order) and already
 * spoiler-filtered — a round that has not passed its reveal hold must never
 * reach this function, or the panel would move the moment the engine settled
 * rather than the moment the clip showed it.
 */
export function buildBirdieRecentForm(
  rounds: readonly BirdiePastRound[],
  window: number = BIRDIE_FORM_WINDOW
): BirdieRecentFormView {
  const counts: Record<string, number> = { IN0: 0, IN1: 0, IN2: 0, IN3: 0 }
  let sampleSize = 0
  let makeRateTotal = 0
  let biggestPaid: number | undefined

  for (const round of rounds) {
    if (sampleSize >= window) break
    const made = holedCount(round)
    if (made === undefined) continue
    const key = `IN${made}`
    if (counts[key] === undefined) continue
    counts[key] += 1
    sampleSize += 1
    makeRateTotal += round.makeRatePct

    // Both markets, because both were bettable: the biggest number on the panel
    // has to be a price somebody could have been on.
    if (round.patternKey !== undefined && round.exactPaid !== undefined) {
      const paid = paidFor(round, round.patternKey, round.exactPaid)
      if (biggestPaid === undefined || paid > biggestPaid) biggestPaid = paid
    }
    if (round.countKey !== undefined && round.countPaid !== undefined) {
      const paid = paidFor(round, round.countKey, round.countPaid)
      if (biggestPaid === undefined || paid > biggestPaid) biggestPaid = paid
    }
  }

  const rows: BirdieFormRow[] = BIRDIE_FORM_KEYS.map((key) => {
    const count = counts[key] ?? 0
    return {
      key,
      marks: glyphsForKey(key) ?? [],
      count,
      fraction: sampleSize === 0 ? 0 : count / sampleSize
    }
  })

  return {
    sampleSize,
    rows,
    avgMakeRate: sampleSize === 0 ? 0 : makeRateTotal / sampleSize / 100,
    ...(biggestPaid === undefined ? {} : { biggestPaid })
  }
}
